<template>
  <div>
    <div class="audiences-config">
      <div class="audiences-config__tablewrap">
        <div class="audiences-config__cardbody">
          <div class="audiences-config__cardhead">
            <div class="audiences-config__search">
              <i class="bi bi-search audiences-config__searchicon" />
              <input
                v-model="audiencesSearchQuery"
                type="text"
                class="form-control audiences-config__searchinput"
                placeholder="Search audiences..."
              >
              <button
                v-if="audiencesSearchQuery"
                type="button"
                class="btn btn-link audiences-config__clear"
                title="Clear search"
                @click="audiencesSearchQuery = ''"
              >
                <i class="bi bi-x-circle" />
              </button>
            </div>

            <button
              class="btn btn-primary btn-sm audiences-config__primary d-flex align-items-center btn-library"
              @click="showAudienceModal('create')"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="mr-1"
              >
                <path d="M5 12h14" />
                <path d="M12 5v14" />
              </svg>
              Add New Audience
            </button>
          </div>

          <div class="audiences-config__tablebox">
            <div class="audiences-config__tableinner">
              <template v-if="isLoadingAudiences || audiences.length > 0">
                <table class="audiences-table mb-0">
                  <thead>
                    <tr>
                      <th
                        class="sortable audiences-table__th audiences-table__th--audience"
                        @click="sortAudiences('name')"
                      >
                        Audience
                        <i
                          v-if="audiencesSortBy === 'name'"
                          class="fas"
                          :class="{
                            'fa-sort-up': audiencesSortOrder === 'asc',
                            'fa-sort-down': audiencesSortOrder === 'desc',
                          }"
                        />
                      </th>
                      <th
                        class="sortable audiences-table__th audiences-table__th--segment"
                        @click="sortAudiences('segment')"
                      >
                        Segment
                        <i
                          v-if="audiencesSortBy === 'segment'"
                          class="fas"
                          :class="{
                            'fa-sort-up': audiencesSortOrder === 'asc',
                            'fa-sort-down': audiencesSortOrder === 'desc',
                          }"
                        />
                      </th>
                      <th
                        class="sortable audiences-table__th audiences-table__th--category"
                        @click="sortAudiences('category')"
                      >
                        Category
                        <i
                          v-if="audiencesSortBy === 'category'"
                          class="fas"
                          :class="{
                            'fa-sort-up': audiencesSortOrder === 'asc',
                            'fa-sort-down': audiencesSortOrder === 'desc',
                          }"
                        />
                      </th>
                      <th
                        class="sortable audiences-table__th audiences-table__th--brand"
                        @click="sortAudiences('brand')"
                      >
                        Brand
                        <i
                          v-if="audiencesSortBy === 'brand'"
                          class="fas"
                          :class="{
                            'fa-sort-up': audiencesSortOrder === 'asc',
                            'fa-sort-down': audiencesSortOrder === 'desc',
                          }"
                        />
                      </th>
                      <th
                        class="sortable audiences-table__th audiences-table__th--description"
                        @click="sortAudiences('description')"
                      >
                        Description
                        <i
                          v-if="audiencesSortBy === 'description'"
                          class="fas"
                          :class="{
                            'fa-sort-up': audiencesSortOrder === 'asc',
                            'fa-sort-down': audiencesSortOrder === 'desc',
                          }"
                        />
                      </th>
                      <th
                        class="audiences-table__th audiences-table__th--actions"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="isLoadingAudiences">
                      <td 
                        colspan="6" 
                        class="text-center"
                      >
                        <span
                          class="spinner-grow spinner-grow-sm"
                          role="status"
                          aria-hidden="true"
                        />
                        Loading...
                      </td>
                    </tr>
                    <tr
                      v-else-if="
                        sortedAudiences.length === 0 && audiencesSearchQuery
                      "
                    >
                      <td 
                        colspan="6" 
                        class="text-center"
                      >
                        <div class="py-4">
                          <i class="fas fa-search fa-3x text-muted mb-3" />
                          <p class="text-muted">
                            No audiences found matching "{{
                              audiencesSearchQuery
                            }}".
                          </p>
                          <button
                            class="btn btn-secondary"
                            @click="audiencesSearchQuery = ''"
                          >
                            Clear Search
                          </button>
                        </div>
                      </td>
                    </tr>
                    <template v-else>
                      <tr
                        v-for="audience in sortedAudiences"
                        :key="audience.id"
                      >
                        <td
                          class="audiences-table__td audiences-table__td--name"
                        >
                          {{ audience.name }}
                        </td>
                        <td
                          class="audiences-table__td audiences-table__td--muted audiences-table__td--segment"
                          :title="audience.segment || '-'"
                        >
                          {{ audience.segment || "-" }}
                        </td>
                        <td
                          class="audiences-table__td audiences-table__td--muted audiences-table__td--category"
                          :title="audience.category || '-'"
                        >
                          {{ audience.category || "-" }}
                        </td>
                        <td
                          class="audiences-table__td audiences-table__td--muted audiences-table__td--brand"
                          :title="audience.brand || '-'"
                        >
                          {{ audience.brand || "-" }}
                        </td>
                        <td
                          class="audiences-table__td audiences-table__td--muted audiences-table__td--description"
                          :title="audience.description || '-'"
                        >
                          <div
                            class="audiences-table__cell audiences-table__cell--ellipsis"
                          >
                            {{ audience.description || "-" }}
                          </div>
                        </td>
                        <td
                          class="audiences-table__td audiences-table__td--actions text-right"
                        >
                          <div
                            class="d-flex audiences-config__actions"
                            role="group"
                          >
                            <button
                              class="audiences-config__actionbtn audiences-config__actionbtn--edit"
                              title="Edit"
                              @click="editAudience(audience)"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                aria-hidden="true"
                              >
                                <path
                                  d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                                />
                                <path
                                  d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"
                                />
                              </svg>
                              Edit
                            </button>
                            <button
                              class="audiences-config__actionbtn audiences-config__actionbtn--delete"
                              title="Delete"
                              @click="deleteAudience(audience)"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                aria-hidden="true"
                              >
                                <path d="M3 6h18" />
                                <path
                                  d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"
                                />
                                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                <line 
                                  x1="10" 
                                  x2="10" 
                                  y1="11" 
                                  y2="17" 
                                />
                                <line 
                                  x1="14" 
                                  x2="14" 
                                  y1="11" 
                                  y2="17" 
                                />
                              </svg>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </template>
              <table 
                v-else 
                class="audiences-table mb-0"
              >
                <thead>
                  <tr>
                    <th
                      class="audiences-table__th audiences-table__th--audience"
                    >
                      Audience
                    </th>
                    <th
                      class="audiences-table__th audiences-table__th--segment"
                    >
                      Segment
                    </th>
                    <th
                      class="audiences-table__th audiences-table__th--category"
                    >
                      Category
                    </th>
                    <th class="audiences-table__th audiences-table__th--brand">
                      Brand
                    </th>
                    <th
                      class="audiences-table__th audiences-table__th--description"
                    >
                      Description
                    </th>
                    <th
                      class="audiences-table__th audiences-table__th--actions"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td 
                      colspan="6" 
                      class="text-center"
                    >
                      <div class="py-4 d-flex flex-column align-items-center">
                        <i
                          class="bi bi-people"
                          style="font-size: 3rem; color: #e2e8f0"
                        />
                        <button
                          class="btn btn-primary btn-library mt-3"
                          @click="showAudienceModal('create')"
                        >
                          <i class="bi bi-plus-circle mr-1" />
                          Create Your First Audience
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Audience Modal -->
    <b-modal
      id="audience-modal"
      v-model="showAudienceModalFlag"
      size="lg"
      header-class="border-bottom-0 pb-0"
      footer-class="border-top-0 pt-0"
      cancel-variant="outline-secondary"
      :ok-title="`Save ${audienceModalMode === 'create' ? '' : 'Changes'}`"
      :ok-disabled="isSaveAudienceDisabled"
      @ok="saveAudience"
      @hidden="resetAudienceModal"
    >
      <template #modal-title>
        <h5 class="m-0">
          {{
            audienceModalMode === "create"
              ? "Add New Audience"
              : "Edit Audience"
          }}
        </h5>
        <span class="text-muted font-lg">
          Define your target audience with detailed characteristics and demographics.
        </span>
      </template>
      <form @submit.prevent="saveAudience">
        <b-form-row>
          <b-col cols="12">
            <b-form-group
              label="Audience Name *"
              label-class="font-weight-bold"
            >
              <b-input 
                v-model="currentAudience.name" 
                autofocus 
                required 
              />
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group 
              label="Segment" 
              label-class="font-weight-bold"
            >
              <b-input
                v-model="currentAudience.segment"
                placeholder="e.g., Millennials"
              />
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group 
              label="Category" 
              label-class="font-weight-bold"
            >
              <b-input
                v-model="currentAudience.category"
                placeholder="e.g., Fashion"
              />
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group 
              label="Brand" 
              label-class="font-weight-bold"
            >
              <b-input
                v-model="currentAudience.brand"
                placeholder="e.g., Nike"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group
              class="mb-0"
              label="Description *"
              label-class="font-weight-bold"
            >
              <b-textarea
                v-model="currentAudience.description"
                rows="7"
                placeholder="Describe this audience segment..."
                required
              />
            </b-form-group>
          </b-col>
          <b-col 
            cols="12" 
            class="mt-2"
          >
            <b-form-group
              label="Extract from document using AI (optional)"
              label-class="font-weight-bold"
            >
              <div
                class="border rounded py-4 text-center cursor-pointer"
                :class="{
                  'border-primary bg-light': isPdfDragOver,
                  'border-secondary': !isPdfDragOver,
                }"
                @dragover.prevent="onPdfDragOver"
                @dragleave.prevent="onPdfDragLeave"
                @drop.prevent="onPdfDrop"
                @click="$refs.audiencePdfInput.click()"
              >
                <p class="mb-1">
                  <strong>Drag & drop a file here</strong> or click to select
                </p>
                <small class="text-muted d-block">
                  {{ selectedAudiencePdfName || "Supported: PDF, DOCX, PPTX" }}
                </small>
              </div>
              <input
                ref="audiencePdfInput"
                type="file"
                class="d-none"
                accept="application/pdf,.docx,.pptx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                @change="onAudiencePdfChange"
              >
            </b-form-group>
          </b-col>
          <b-col 
            v-if="audiencePdfText" 
            cols="12"
          >
            <b-form-group
              label="Extraction type"
              label-class="font-weight-bold"
            >
              <b-form-select
                v-model="guidelineExtractionType"
                :options="guidelineTypeOptions"
              />
            </b-form-group>
          </b-col>
          <b-col 
            v-if="audiencePdfText" 
            cols="12"
          >
            <b-form-group
              label="Number of guidelines"
              label-class="font-weight-bold"
            >
              <b-form-select
                v-model="guidelineCount"
                :options="guidelineCountOptions"
              />
            </b-form-group>
          </b-col>
          <b-col 
            v-if="audiencePdfText" 
            cols="12"
          >
            <b-button
              variant="primary"
              block
              :disabled="isGeneratingDescription"
              @click="generateGuidelinesFromPdf"
            >
              {{
                isGeneratingDescription
                  ? "Processing..."
                  : "Generate description"
              }}
            </b-button>
          </b-col>
        </b-form-row>
      </form>
    </b-modal>

    <!-- Delete Confirmation Modal -->
    <b-modal
      v-model="showDeleteConfirmModal"
      title="Confirm Delete"
      @ok="confirmDeleteAudience"
    >
      <p>
        Are you sure you want to delete the audience
        <strong>{{ audienceToDelete?.name }}</strong> ?
      </p>
      <p class="text-muted mb-0">
        This action cannot be undone.
      </p>
    </b-modal>
  </div>
</template>
<script>
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
import JSZip from 'jszip';

export default {
  name: "AudiencePage",
  props: {
    showModal: {
      type: Boolean,
      default: false,
      required: false,
    },
    fromAnalysis: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  data() {
    return {
      audiences: [],
      selectedAudiences: [],
      isLoadingAudiences: false,
      audiencesSearchQuery: '',
      audiencesSortBy: 'name',
      audiencesSortOrder: 'asc',
      showAudienceModalFlag: false,
      audienceModalMode: 'create', // 'create' or 'edit'
      currentAudience: {
        id: null,
        name: '',
        segment: '',
        category: '',
        brand: '',
        description: '',
      },
      showDeleteConfirmModal: false,
      audienceToDelete: null,
      creatingFromAnalysisFlow: false,
      isPdfDragOver: false,
      selectedAudiencePdfName: '',
      audiencePdfText: '',
      isParsingPdf: false,
      isGeneratingDescription: false,
      guidelineExtractionType: 'both',
      guidelineTypeOptions: [
        { value: 'brand', text: 'Brand style guidelines' },
        { value: 'demographics', text: 'Demographics' },
        { value: 'both', text: 'Both' },
      ],

      guidelineCount: 'auto',
      guidelineCountOptions: [
        { value: 'auto', text: 'Auto' },
        ...Array.from({ length: 16 }, (_, i) => {
          const num = i + 5;
          return { value: num.toString(), text: num.toString() };
        }),
      ],
    };
  },
  computed: {
    isSaveAudienceDisabled() {
      return !(this.currentAudience.name.trim() && this.currentAudience.description.trim());
    },
    sortedAudiences() {
      let filtered = this.audiences;
      if (this.audiencesSearchQuery) {
        const query = this.audiencesSearchQuery.toLowerCase();
        filtered = this.audiences.filter(audience => {
          const name = (audience.name || '').toLowerCase();
          const description = (audience.description || '').toLowerCase();
          return name.includes(query) || description.includes(query);
        });
      }

      const sorted = [...filtered].sort((a, b) => {
        const aVal = a[this.audiencesSortBy] || '';
        const bVal = b[this.audiencesSortBy] || '';

        if (this.audiencesSortOrder === 'asc') {
          return aVal.localeCompare(bVal);
        } else {
          return bVal.localeCompare(aVal);
        }
      });
      return sorted;
    },
  },
  watch: {
    showModal: {
      immediate: true,
      handler(val) {
        this.showAudienceModalFlag = val;
      }
    },
    fromAnalysis: {
      immediate: true,
      handler(val) {
        this.creatingFromAnalysisFlow = val;
      }
    },
  },
  mounted() {
    this.loadAudiences();
    this.showAudienceModalFlag = this.showModal;
    this.creatingFromAnalysisFlow = this.fromAnalysis;
  },
  methods: {
    async loadAudiences() {
      this.isLoadingAudiences = true;
      try {
        const response = await axios.get('/audiences', {
          params: {
            sort_by: this.audiencesSortBy,
            sort_order: this.audiencesSortOrder,
          }
        });
        if (response.data.success) {
          this.audiences = response.data.data;
        }
      } catch (error) {
        console.error('Error loading audiences:', error);
        toastr.error('Failed to load audiences');
      } finally {
        this.isLoadingAudiences = false;
      }
    },

    sortAudiences(field) {
      if (this.audiencesSortBy === field) {
        this.audiencesSortOrder = this.audiencesSortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.audiencesSortBy = field;
        this.audiencesSortOrder = 'asc';
      }
    },

    showAudienceModal(mode) {
      this.audienceModalMode = mode;
      this.showAudienceModalFlag = true;
    },

    editAudience(audience) {
      this.currentAudience = {
        id: audience.id,
        name: audience.name,
        segment: audience.segment || '',
        category: audience.category || '',
        brand: audience.brand || '',
        description: audience.description || '',
      };
      this.showAudienceModal('edit');
    },

    resetAudienceModal() {
      this.currentAudience = {
        id: null,
        name: '',
        segment: '',
        category: '',
        brand: '',
        description: '',
      };
      this.audienceModalMode = 'create';
    },

    async saveAudience(bvModalEvent) {
      bvModalEvent.preventDefault();

      if (!this.currentAudience.name.trim()) {
        toastr.warning('Please enter an audience name');
        return;
      }

      try {
        let response;
        if (this.audienceModalMode === 'create') {
          response = await axios.post('/audiences', {
            name: this.currentAudience.name,
            segment: this.currentAudience.segment,
            category: this.currentAudience.category,
            brand: this.currentAudience.brand,
            description: this.currentAudience.description,
          });
        } else {
          response = await axios.put(`/audiences/${this.currentAudience.id}`, {
            name: this.currentAudience.name,
            segment: this.currentAudience.segment,
            category: this.currentAudience.category,
            brand: this.currentAudience.brand,
            description: this.currentAudience.description,
          });
        }

        if (response.data.success) {
          toastr.success(response.data.message);
          this.showAudienceModalFlag = false;
          this.resetAudienceModal();

          if (this.audienceModalMode === 'create' && this.creatingFromAnalysisFlow) {
            const newAudience = response.data.data;
            this.audiences.push(newAudience);
            this.selectedAudiences = [newAudience];
            this.$emit('update-audiences-analysis', this.audiences, this.selectedAudiences);
            // this.updateHeaderButton();
            this.creatingFromAnalysisFlow = false;
          } else if (this.audienceModalMode === 'edit') {
            const updatedAudience = response.data.data;

            const audienceIndex = this.audiences.findIndex(a => a.id === updatedAudience.id);
            if (audienceIndex !== -1) {
              this.audiences.splice(audienceIndex, 1, updatedAudience);
            }

            const selectedIndex = this.selectedAudiences.findIndex(a => a.id === updatedAudience.id);
            if (selectedIndex !== -1) {
              this.selectedAudiences.splice(selectedIndex, 1, updatedAudience);
            }

            this.$emit('update-audiences', this.audiences, this.selectedAudiences);
          } else {
            await this.loadAudiences();
            this.$emit('update-audiences', this.audiences, this.selectedAudiences);
          }
        }
      } catch (error) {
        console.error('Error saving audience:', error);
        const message = error.response?.data?.message || 'Failed to save audience';
        toastr.error(message);
      }
    },

    deleteAudience(audience) {
      this.audienceToDelete = audience;
      this.showDeleteConfirmModal = true;
    },

    async confirmDeleteAudience() {
      if (!this.audienceToDelete) return;

      try {
        const response = await axios.delete(`/audiences/${this.audienceToDelete.id}`);

        if (response.data.success) {
          toastr.success(response.data.message);

          const deletedId = this.audienceToDelete.id;
          this.audiences = this.audiences.filter(a => a.id !== deletedId);

          this.selectedAudiences = this.selectedAudiences.filter(a => a.id !== deletedId);
          this.$emit('update-audiences-analysis', this.audiences, this.selectedAudiences);
        }
      } catch (error) {
        console.error('Error deleting audience:', error);
        const message = error.response?.data?.message || 'Failed to delete audience';
        toastr.error(message);
      } finally {
        this.audienceToDelete = null;
      }
    },

    onPdfDragOver() {
      this.isPdfDragOver = true;
    },

    onPdfDragLeave() {
      this.isPdfDragOver = false;
    },

    onPdfDrop(event) {
      this.isPdfDragOver = false;
      const files = event.dataTransfer?.files;
      if (!files || !files.length) return;

      const file = files[0];
      this.handleAudiencePdfSelected(file);
    },

    onAudiencePdfChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.handleAudiencePdfSelected(file);
      event.target.value = '';
    },

    async handleAudiencePdfSelected(file) {
      const allowedTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      ];
      const allowedExtensions = ['.pdf', '.docx', '.pptx'];
      const fileExtension = file?.name?.toLowerCase().slice(file.name.lastIndexOf('.'));

      if (!file || (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension))) {
        console.warn('Only PDF, DOCX, and PPTX files are allowed');
        return;
      }

      this.selectedAudiencePdfName = file.name;
      this.isParsingPdf = true;

      try {
        let text = '';
        if (file.type === 'application/pdf' || fileExtension === '.pdf') {
          text = await this.extractTextFromPdf(file);
        } else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || fileExtension === '.docx') {
          text = await this.extractTextFromDocx(file);
        } else if (file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' || fileExtension === '.pptx') {
          text = await this.extractTextFromPptx(file);
        }
        this.audiencePdfText = text;
      } catch (error) {
        console.error('Error parsing document', error);
      } finally {
        this.isParsingPdf = false;
      }
    },
    async extractTextFromPdf(file) {
      const arrayBuffer = await file.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;

      let fullText = '';

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const content = await page.getTextContent();

        const pageText = content.items.map(i => i.str).join(' ');
        fullText += pageText + '\n\n';
      }

      return fullText.trim();
    },
    async extractTextFromDocx(file) {
      const arrayBuffer = await file.arrayBuffer();
      const zip = await JSZip.loadAsync(arrayBuffer);

      const documentXml = await zip.file('word/document.xml')?.async('string');
      if (!documentXml) {
        throw new Error('Could not find document.xml in DOCX file');
      }

      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(documentXml, 'application/xml');

      const paragraphs = xmlDoc.getElementsByTagName('w:p');
      let fullText = '';

      for (let i = 0; i < paragraphs.length; i++) {
        const paragraph = paragraphs[i];
        const textNodes = paragraph.getElementsByTagName('w:t');
        let paragraphText = '';

        for (let j = 0; j < textNodes.length; j++) {
          paragraphText += textNodes[j].textContent || '';
        }

        if (paragraphText.trim()) {
          fullText += paragraphText + '\n';
        }
      }

      return fullText.trim();
    },

    async extractTextFromPptx(file) {
      const arrayBuffer = await file.arrayBuffer();
      const zip = await JSZip.loadAsync(arrayBuffer);

      let fullText = '';
      const slideFiles = Object.keys(zip.files)
        .filter(name => name.match(/^ppt\/slides\/slide\d+\.xml$/))
        .sort((a, b) => {
          const numA = parseInt(a.match(/slide(\d+)\.xml$/)[1]);
          const numB = parseInt(b.match(/slide(\d+)\.xml$/)[1]);
          return numA - numB;
        });

      for (const slideFile of slideFiles) {
        const slideXml = await zip.file(slideFile)?.async('string');
        if (!slideXml) continue;

        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(slideXml, 'application/xml');

        const textNodes = xmlDoc.getElementsByTagName('a:t');
        let slideText = '';

        for (let i = 0; i < textNodes.length; i++) {
          slideText += (textNodes[i].textContent || '') + ' ';
        }

        if (slideText.trim()) {
          fullText += slideText.trim() + '\n\n';
        }
      }

      return fullText.trim();
    },
    async generateGuidelinesFromPdf() {
      if (!this.audiencePdfText) return;

      this.isGeneratingDescription = true;

      try {
        const prompt = this.buildPrompt();
        const openAiResponse = await this.callOpenAI({
          model: 'gemini-2.5-flash',
          messages: [
            {
              role: 'user',
              content: prompt
            }
          ]
        });

        this.currentAudience.description = openAiResponse.content;
      } catch (error) {
        console.error('Error generating guidelines', error);
      } finally {
        this.isGeneratingDescription = false;
      }
    },
    buildPrompt() {
      let intro = '';
      if (this.guidelineExtractionType === 'brand') {
        intro = `Extract brand style guidelines from the following document.`;
      } else if (this.guidelineExtractionType === 'demographics') {
        intro = `Extract demographic guidelines describing the targeted shoppers or audience segments from the following document.`;
      } else {
        intro = `Extract both brand style guidelines and demographic guidelines from the document.`;
      }

      const count =
        this.guidelineCount === 'auto'
          ? 'Use your best judgment to determine how many guidelines are relevant.'
          : `Extract the ${this.guidelineCount} most important guidelines.`;

      return `
${intro}
${count}
Return only the bullet list with no additional text or headings.
Use "-" for each bullet.

-----

Document Content:
${this.audiencePdfText}
      `.trim();
    },
    async callOpenAI(settings) {
      let settingsCopy = JSON.parse(JSON.stringify(settings));
      try {
        const response = await axios.post(`/call-open-ai`, this.prepareApiRequest(settingsCopy));

        if (response.data && typeof response.data === "object") {
          return response.data;
        } else {
          console.warn("Unexpected API response format:", response);
          throw new Error("Invalid API response format");
        }
      } catch (error) {
        if (error.name === "AbortError") {
          throw new Error("API request timed out");
        }

        const errorMessage =
          error.response?.data?.error?.message ||
          error.message ||
          "Unknown API error";

        throw new Error(errorMessage);
      }
    },
    prepareApiRequest(settings) {
       if (settings.messages && Array.isArray(settings.messages) && Array.isArray(settings.messages[0]?.content)) {
         settings.messages[0].content.forEach(item => {
           if (item.type === 'text') {
             const imagesCount = settings.messages[0].content.filter(m => m.type === 'image_url').length;
             item.text = item.text.replaceAll("[images_count]", imagesCount).replaceAll('{{image_count}}', imagesCount);
           }
           
           if (item.type === 'image_url') {
             delete item.image_url.highResUrl;
           }
         })
       }
       
       return settings;
    },
  }
};
</script>

<style scoped lang="scss">
.configure-audiences-btn {
  border-color: #dee2e6;
  color: #212529;

  &:hover {
    background-color: #f8f9fa;
    border-color: #dee2e6;
    color: #212529;
  }
}

.audiences-config {
  padding: 0 0 6px;
}

.audiences-config__backrow {
  margin: 2px 0 10px;
}

.audiences-config__cardbody {
  padding: 24px;
}

.audiences-config__cardhead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 24px;
}

.audiences-config__back {
  color: #1abc9c;
  text-decoration: none;
  transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
  transition-timing-function: cubic-bezier(.4, 0, .2, 1);
  transition-duration: .15s;
  padding-top: .5rem;
  padding-bottom: .5rem;
  font-weight: 500;
  font-size: .875rem;
  line-height: 1.25rem;
  padding-left: 1rem;
  padding-right: 1rem;
  border-radius:6px;

  &:hover {
    background-color: #f1f5f9;
    color: #1abc9ccc;
    text-decoration: none;
  }
}

.audiences-config__back svg {
  width: 16px;
  height: 16px;
}

.audiences-config__search {
  position: relative;
  flex: 1 1 320px;
  max-width: 340px;
}

.audiences-config__searchicon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.audiences-config__searchinput {
  padding: .5rem .75rem .5rem 2.5rem;
  border-color: #e2e8f0;
  height: 40px;
  border-radius:6px;
  font-size: .875rem;
  line-height: 1.25rem; 

  &:focus {
    border-color: #bfc7d1;
    box-shadow: none;
  }
}

.audiences-config__clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  padding: 0;
  color: #94a3b8;
  text-decoration: none;

  &:hover {
    color: #64748b;
    text-decoration: none;
  }
}

.audiences-config__primary {
  transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
  transition-timing-function: cubic-bezier(.4, 0, .2, 1);
  transition-duration: .15s;
  color: #ffffff;
  border-radius: 6px;
  border-color: #1abc9c;
  font-weight: 500;
  font-size: .875rem;
  line-height: 1.25rem;
  padding-top: .5rem;
  padding-bottom: .5rem;
  padding-left: 1rem;
  padding-right: 1rem;

  &:hover {
    border-color: #1abc9ce6;
  }

  &:focus,
  &:active,
  &:focus:active {
    outline: none !important;
    box-shadow: none !important;
  }
}

.audiences-config__primary svg{
  width: 16px;
  height: 16px;
}

.audiences-config__tablewrap {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}

.audiences-config__tablebox {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
}

.audiences-config__tableinner {
  width: 100%;
  overflow: auto;
}

.audiences-config__actions {
  gap: 8px;
  justify-content: flex-end;
}

.audiences-config__actionbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  white-space: nowrap;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25rem;
  padding: 0 12px;
  height: 36px;
  transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease;
  border: 1px solid transparent;
}

.audiences-config__actionbtn svg {
  width: 12px;
  height: 12px;
}

.audiences-config__actionbtn--edit {
  border-color: #e2e8f0;
  background: #ffffff;
  color: #0f172a;

  &:hover {
    background: #f1f5f9;
  }
}

.audiences-config__actionbtn--delete {
  background: #ef4444;
  border-color: #ef4444;
  color: #ffffff;

  &:hover {
    background: #dc2626;
    border-color: #dc2626;
    color: #ffffff;
  }
}
.audiences-config .audiences-table thead th {
  position: static !important;
  top: auto !important;
  z-index: auto !important;
  border-top: none !important;
}

.audiences-config .audiences-table thead th {
  position: static !important;
}

.audiences-config .audiences-table thead tr {
  border-bottom: 1px solid #e5e7eb;
}

.audiences-config .audiences-table thead th {
  border-bottom: none;
}

.audiences-config .audiences-table {
  width: 100%;
  caption-side: bottom;
  text-indent: 0;
  border-color: inherit;
  border-collapse: collapse;
  table-layout: fixed;
  background: #ffffff;
  font-size: 0.875rem;
  line-height: 1.25rem;
}

.audiences-table td,
.audiences-table th {
  padding: 0.8rem 0.95rem;
  border-color: #eef2f7;
}

.audiences-config .audiences-table thead tr {
  border-bottom: 1px solid #e5e7eb;
}

.audiences-config .audiences-table thead th {
  height: 48px;
  padding: 0 16px;
  text-align: left;
  vertical-align: middle;
  font-weight: 500;
  color: #6b7280;
}

.audiences-config .audiences-table thead th.audiences-table__th--actions {
  text-align: right;
  width: 120px;
}

.audiences-config .audiences-table__td--actions {
  width: 120px;
  white-space: nowrap;
}

.audiences-config .audiences-table__th--audience {
  width: 200px;
}
.audiences-config .audiences-table__th--segment,
.audiences-config .audiences-table__th--category,
.audiences-config .audiences-table__th--brand {
  width: 120px;
}

.audiences-config .audiences-table__th--description {
  width: 360px;
}

.audiences-config .audiences-table__td--description {
  width: 360px;
}

.audiences-config .audiences-table tbody tr {
  border-bottom: 1px solid #e5e7eb;
  transition: background-color 0.15s ease;
}

.audiences-config .audiences-table tbody tr:last-child {
  border-bottom: 0;
}

.audiences-config .audiences-table tbody tr:hover {
  background: #f1f5f9;
}

.audiences-config .audiences-table td {
  padding: 16px;
  vertical-align: middle;
}

.audiences-config .audiences-table__td--name {
  font-weight: 500;
  color: #0f172a;
}

.audiences-config .audiences-table__td--muted {
  color: #6b7280;
}

.audiences-config .audiences-table__td--segment,
.audiences-config .audiences-table__td--category,
.audiences-config .audiences-table__td--brand {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.audiences-config .audiences-table__td--description {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.audiences-config .audiences-table__cell {
  display: block;
  min-width: 0;
}

.audiences-config .audiences-table__cell--ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.audiences-table .sortable {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

.audiences-table .sortable:hover {
  background-color: #e9ecef;
}

.audiences-table .sortable i {
  margin-left: 0.5rem;
  font-size: 0.875rem;
  opacity: 0.6;
}

.audiences-table td,
.audiences-table th {
  vertical-align: middle;
}

.audiences-table tbody tr:nth-child(odd) {
  background-color: #f8f9fa;
}
</style>